import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Pill, BookOpen, FileText, ClipboardCheck, Activity, Sparkles } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const FEATURES = [
  { path: "/vitamin", title: "Vitamin Recommender", icon: Pill, description: "Personalised UK vitamin recommendations based on NHS and SACN guidance", color: "text-nhs-blue" },
  { path: "/literature", title: "Literature Q&A", icon: BookOpen, description: "Query UK clinical literature — NICE, BNF, NHS, MHRA sources", color: "text-nhs-brightblue" },
  { path: "/report", title: "Report Generation", icon: FileText, description: "Generate microbiome, genomic, and clinical study reports", color: "text-nhs-green" },
  { path: "/regulatory", title: "Regulatory Drafts", icon: ClipboardCheck, description: "Draft protocols, IBs, NICE submissions, CE technical files", color: "text-nhs-purple" },
  { path: "/interpret", title: "Data Interpretation", icon: Activity, description: "Interpret biomarker, genomic, and clinical lab data", color: "text-nhs-darkblue" },
];

export default function HomePage() {
  const { email, username } = useAuth();
  const displayName = email?.split("@")[0] || username || "researcher";

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-nhs-blue to-nhs-darkblue text-white rounded-lg p-8"
      >
        <div className="flex items-center gap-2 text-blue-200 text-sm mb-2">
          <Sparkles size={16} />
          <span>Powered by Claude Opus 4.7</span>
        </div>
        <h1 className="text-3xl font-bold mb-2">Welcome back, {displayName}</h1>
        <p className="text-blue-100 max-w-2xl">
          AlphaYou provides NHS-aligned medical intelligence for clinicians, researchers, and UK life sciences teams.
          All outputs grounded in NICE guidelines, BNF, SACN, and MHRA sources.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((feature, idx) => (
          <motion.div
            key={feature.path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Link to={feature.path} className="card hover:shadow-md transition-shadow h-full block">
              <feature.icon className={`${feature.color} mb-3`} size={32} />
              <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="card bg-yellow-50 border-yellow-200">
        <p className="text-sm text-yellow-900">
          <strong>Clinical disclaimer:</strong> All outputs from this platform are AI-generated and must be verified against
          primary sources. Not a substitute for professional medical advice. Consult a GP or pharmacist before acting on any recommendation.
        </p>
      </div>
    </div>
  );
}
